/**
 * Calibration aggregates for tagged `email_inbox_log` rows.
 * @see docs/APP_UI_SPEC.md — D3
 */

/** @type {[number, number][]} each [lo, hi) on confidence; last bucket is [0.95, 1] inclusive */
export const CONFIDENCE_BUCKET_BOUNDS = [
  [0, 0.5],
  [0.5, 0.7],
  [0.7, 0.85],
  [0.85, 0.95],
  [0.95, 1],
];

export const CALIBRATION_MIN_ACCURACY = 0.99;
export const CALIBRATION_MIN_BUCKET_SAMPLE = 20;

/**
 * @param {number} confidence
 * @returns {number} bucket index 0..length-1
 */
export function confidenceBucketIndex(confidence) {
  const x = Number(confidence);
  if (Number.isNaN(x) || x < 0) return 0;
  if (x < 0.5) return 0;
  if (x < 0.7) return 1;
  if (x < 0.85) return 2;
  if (x < 0.95) return 3;
  return 4;
}

export function confidenceBucketLabel(bucketIndex) {
  const labels = ['[0, 0.5)', '[0.5, 0.7)', '[0.7, 0.85)', '[0.85, 0.95)', '[0.95, 1]'];
  return labels[bucketIndex] || '';
}

/**
 * @param {Array<{ parsed_intent: string|null, confidence: number|null, ground_truth_intent: string }>} rows
 * @param {string[]} intentColumns - which parsed intents to show as columns
 * @returns {{ bucketIndex: number, label: string, byIntent: Record<string, { n: number, agree: number }> }[]}
 */
export function buildCalibrationTableRows(rows, intentColumns) {
  const table = CONFIDENCE_BUCKET_BOUNDS.map((_, bucketIndex) => ({
    bucketIndex,
    label: confidenceBucketLabel(bucketIndex),
    byIntent: Object.fromEntries(intentColumns.map((i) => [i, { n: 0, agree: 0 }])),
  }));

  for (const row of rows) {
    const gt = row.ground_truth_intent;
    const parsed = row.parsed_intent;
    if (!gt || parsed == null) continue;
    const bi = confidenceBucketIndex(row.confidence ?? 0);
    if (!intentColumns.includes(parsed)) continue;
    const cell = table[bi].byIntent[parsed];
    cell.n += 1;
    if (String(parsed).toLowerCase() === String(gt).toLowerCase()) cell.agree += 1;
  }

  return table;
}

/**
 * Overall agreement on tagged rows (parsed vs ground truth).
 */
export function calibrationOverallAgreement(rows) {
  let n = 0;
  let agree = 0;
  for (const row of rows) {
    const gt = row.ground_truth_intent;
    const parsed = row.parsed_intent;
    if (!gt || parsed == null) continue;
    n += 1;
    if (String(parsed).toLowerCase() === String(gt).toLowerCase()) agree += 1;
  }
  return { n, agree, pct: n === 0 ? null : Math.round((1000 * agree) / n) / 10 };
}

/**
 * Highest confidence bucket (by index, high = more confident) where `intent` has n>=minN and agree/n >= minAcc.
 * Returns label + stats for summary line.
 */
export function findHighestReliableBucketForIntent(rows, intent, minAcc, minN) {
  for (let bi = CONFIDENCE_BUCKET_BOUNDS.length - 1; bi >= 0; bi--) {
    let n = 0;
    let agree = 0;
    for (const row of rows) {
      if (String(row.parsed_intent || '').toLowerCase() !== intent) continue;
      if (confidenceBucketIndex(row.confidence ?? 0) !== bi) continue;
      const gt = row.ground_truth_intent;
      if (!gt) continue;
      n += 1;
      if (String(row.parsed_intent).toLowerCase() === String(gt).toLowerCase()) agree += 1;
    }
    if (n >= minN && n > 0 && agree / n >= minAcc) {
      const [lo] = CONFIDENCE_BUCKET_BOUNDS[bi];
      return { bucketIndex: bi, lo, n, agree, label: confidenceBucketLabel(bi) };
    }
  }
  return null;
}
